import * as Yup from 'yup';
import { v4 as uuidV4 } from 'uuid';
import { ICommodity } from '../commodity/commodity';
import { IOptionType } from '@zz2/zz2-ui';
import { ICommodityFormValues } from './commodityFormValues';
import { ICommodityUpsert } from './commodityUpsert';
import { IOrganization } from '../organization/organization';
import { ICommodityCondition } from '../commodityCondition/commodityCondition';
import { ICommodityConditionRelUpsert } from '../commodityConditionRel/commodityConditionRelUpsert';
import CommodityConditionRelModelHelper from '../commodityConditionRel/commodityConditionRelModelHelper';
import { ICommodityConditionRelFormValues } from '../commodityConditionRel/commodityConditionRelFormValues';
import { isNullOrWhiteSpace } from '../../../../services/appFunctionsService';

export default class CommodityModelHelper {
    public static createFormValues = (
        commodity : ICommodity | null,
        organization : Array<IOrganization>,
        commodities : Array<ICommodity>,
        commodityConditions : Array<ICommodityCondition>,
    ) : ICommodityFormValues => {
        if (!commodity) return {
            id: 0,
            guid: uuidV4(),
            code: '',
            name: '',
            exportCode: null,
            temperature: null,
            temperatureRegime: null,
            organizations: [],
            commodityConditionBridges: [],
            isPhytoCompliant: false,
            isStoneFruit: false,
            isActive: true,
        };

        const organizationOptions : Array<IOptionType> = organization
            .filter(x => x.isActive && commodity.organizationIds.includes(x.id))
            .map(x => { // TODO: OptionType.FromDataModel
                return {
                    value: x.id,
                    label: `(${x.code}) - ${x.name}`,
                };
            });

        return {
            id: commodity.id,
            guid: commodity.guid,
            code: commodity.code,
            name: commodity.name ?? '',
            exportCode: commodity.exportCode,
            temperature: commodity.temperature,
            temperatureRegime: commodity.temperatureRegime,
            isPhytoCompliant: commodity.isPhytoCompliant,
            isStoneFruit: commodity.isStoneFruit,
            organizations: organizationOptions,
            commodityConditionBridges: commodity.commodityConditions
                .map(x => CommodityConditionRelModelHelper.createFormValues(x, commodities, commodityConditions)),
            isActive: commodity.isActive,
        };
    };

    public static createUpsert = (formValues : ICommodityFormValues) : ICommodityUpsert => {
        return {
            id: formValues.id,
            guid: formValues.guid,
            code: formValues.code,
            name: formValues.name,
            exportCode: isNullOrWhiteSpace(formValues.exportCode) ? null : formValues.exportCode,
            temperature: isNullOrWhiteSpace(formValues.temperature) ? null : formValues.temperature,
            temperatureRegime: isNullOrWhiteSpace(formValues.temperatureRegime) ? null : formValues.temperatureRegime,
            isPhytoCompliant: formValues.isPhytoCompliant,
            isStoneFruit: formValues.isStoneFruit,
            organizationIds: formValues.organizations.map(x => Number(x.value)),
            conditionUpserts: formValues.commodityConditionBridges.map(CommodityConditionRelModelHelper.createUpsert),
            isActive: formValues.isActive,
        };
    };

    public static formSchema = () : Yup.ObjectSchema<ICommodityFormValues> => Yup.object({
        id: Yup.number().typeError('Please enter a number.').required('Required'),
        guid: Yup.string().required('Required'),
        code: Yup.string().trim().min(1, 'Must have at least 1 character').required('Required'),
        name: Yup.string().trim().min(1, 'Must have at least 1 character').required('Required'),
        exportCode: Yup.string().trim().defined().nullable()
            .transform((value, originalValue) => 
                isNullOrWhiteSpace(originalValue) ? null : value
            ),
        temperature: Yup.string().trim().defined().nullable()
            .transform((value, originalValue) => 
                isNullOrWhiteSpace(originalValue) ? null : value
            ),
        temperatureRegime: Yup.string().trim().defined().nullable()
            .transform((value, originalValue) => 
                isNullOrWhiteSpace(originalValue) ? null : value
            ),
        organizations: Yup.array().defined().required('Required'),
        commodityConditionBridges: Yup.array<ICommodityConditionRelFormValues>().defined().required('Required'),
        isPhytoCompliant: Yup.boolean().required('Required'),
        isStoneFruit: Yup.boolean().required('Required'),
        isActive: Yup.boolean().required('Required'),
    });

    public static createConditionUpsert = (commodityConditionOptions : Array<IOptionType>, selectedCommodity : ICommodity | null) : Array<ICommodityConditionRelUpsert> => {
        const existingConditions = selectedCommodity?.commodityConditions ?? [];
        
        return commodityConditionOptions.map((x, index) => {
            const existingCondition = existingConditions.find(y => y.order === index + 1);
            
            return {
                id: existingCondition?.id ?? 0,
                guid: existingCondition?.guid ?? uuidV4(),
                isActive: true,

                order: index + 1,
                commodityId: selectedCommodity?.id ?? 0,
                isSpacer: x.label.includes('<spacer>'),
                commodityConditionId: Number(x.value) > 0 ? Number(x.value) : null,
            };
        });
    };
}
